import React, { memo, useMemo } from 'react';
import { SignerWidgetForm } from './SignerBlock';
import { FormHTML } from '@core/components/form/FormHTML';
import { FormControl } from '@core/utils/form';
import { Label } from 'reactstrap';
import { documentHref } from '@business/services/proposal_service/signer_documents_service';
import { openAttachDocumentModal } from './AttachDocumentModal';

export interface SignerBlockSettingsProps {
  form: SignerWidgetForm;
  terms?: FormControl<string>;
}

export const SignerBlockSettings = memo((props: SignerBlockSettingsProps) => {
  const { terms } = props;

  /**
   * "Link Document" toolbar action: pick a Public Page / Agreement and turn the selected words into
   * a link to it. The href is a marker (see `documentHref`) that the signer block resolves to the
   * real, value-filled document when the proposal is viewed.
   */
  const documentButton = useMemo(
    () => [
      {
        // Rendered as a text label (no icon) like the neighbouring toolbar actions — a second link
        // glyph next to Jodit's own "Insert link" was indistinguishable.
        name: 'Document',
        tooltip: 'Link the selected text to a Public Page or Agreement',
        exec: (editor: any) => {
          // The picker is a modal, so the caret/selection is gone by the time it resolves — save it
          // here and restore it before writing the link.
          const markers = editor.s.save();
          openAttachDocumentModal({
            onDone: documents => {
              const document = documents[0];
              if (!document) return;
              const href = documentHref(document.templateId);

              editor.s.focus();
              try {
                editor.s.restore(markers);
              } catch {
                // Selection could not be restored (editor re-rendered) — fall through to insert.
              }

              if (editor.s.isCollapsed?.()) {
                // Nothing was selected: drop in the document's name as the link text.
                editor.s.insertHTML(`<a href="${href}" target="_blank" rel="noopener noreferrer">${document.name}</a>`);
              } else {
                // Wrap the selection. Building an <a> from editor.s.html would replace the selected
                // words with the label instead of linking them.
                editor.execCommand('createlink', false, href);
              }

              // createlink writes no target/rel — set them so the author's own preview opens a tab too.
              editor.editor?.querySelectorAll(`a[href="${href}"]`)?.forEach((anchor: HTMLAnchorElement) => {
                anchor.setAttribute('target', '_blank');
                anchor.setAttribute('rel', 'noopener noreferrer');
              });
              editor.setEditorValue();
            },
          });
        },
      },
    ],
    [],
  );

  if (!terms) return null;

  return (
    <div>
      <Label className="font-weight-bold">Terms &amp; Conditions</Label>
      <p className="text-muted font-size-12 mb-2">
        Style this however you like. Type <code>[]</code> where the signer should tick a box, and select any words then press{' '}
        <strong>Document</strong> to link them to a Public Page or Agreement it opens in a new tab with this proposal's details already
        filled in.
      </p>
      <FormHTML control={terms} minimal extraButtons={documentButton} />
    </div>
  );
});
